import React from "react";
import { Tag, Descriptions, Empty, Typography, Divider } from "antd";
import type { FieldDef } from "@evo-flywheel/contracts";
import type { RefineResource } from "@kit/refine-resources";
import { renderCell } from "../../fields";

const { Text } = Typography;

/**
 * Descriptor-driven wide-panel content for a Show page.
 *
 * Instead of replicating the left assistant panel, this surfaces the governance
 * signals that matter for the record being viewed: AI-Act risk classification,
 * approval/workflow state, review dates, usage projections, and outbound
 * relations. It is generic — it reads the resource's field catalog and shows
 * only the sections whose fields exist, so every one of the 12 entities gets a
 * meaningful panel without per-entity code.
 */

const RISK_COLORS: Record<string, string> = {
  inaccettabile: "red",
  alto: "volcano",
  limitato: "gold",
  minimo: "green",
};

const STATUS_COLORS: Record<string, string> = {
  approvato: "green",
  "in valutazione": "processing",
  respinto: "red",
  dismesso: "default",
  bozza: "default",
  inviata: "processing",
  accettata: "green",
  rifiutata: "red",
};

const RISK_FIELDS = [
  "categoria_rischio",
  "categoria_rischio_proposta",
  "categoria_rischio_confermata",
];
const STATUS_FIELDS = [
  "stato_approvazione",
  "stato_richiesta",
  "stato_avanzamento",
  "stato_utente",
  "esito",
];
const SIGNAL_FIELDS = [
  "prossima_revisione",
  "ultima_interazione",
  "contatore_utilizzo",
  "data_scadenza",
  "valutazione_privacy",
];

function tagFor(value: string, palette: Record<string, string>): React.ReactNode {
  const color = palette[value.trim().toLowerCase()];
  return <Tag color={color}>{value}</Tag>;
}

interface GovernancePanelProps {
  meta: RefineResource["meta"] | undefined;
  record: Record<string, unknown> | undefined;
}

export const GovernancePanel: React.FC<GovernancePanelProps> = ({ meta, record }) => {
  if (!meta || !record) {
    return (
      <div style={{ padding: 24 }}>
        <Empty description="No governance data" />
      </div>
    );
  }

  const fieldMap = new Map<string, FieldDef>(meta.fields.map((f) => [f.name, f]));
  const has = (name: string) => fieldMap.has(name) && record[name] != null && record[name] !== "";
  const labelOf = (name: string) => fieldMap.get(name)?.label ?? name;

  const riskField = RISK_FIELDS.find(has);
  const statusFields = STATUS_FIELDS.filter(has);
  const signalFields = SIGNAL_FIELDS.filter(has);
  const relationFields = meta.fields.filter(
    (f) => f.type === "ref" && has(f.name) && !f.name.endsWith("_id"),
  );

  const nothing =
    !riskField && statusFields.length === 0 && signalFields.length === 0 && relationFields.length === 0;

  return (
    <div style={{ padding: "48px 16px 16px" }}>
      <Text strong style={{ fontSize: 13, textTransform: "uppercase", letterSpacing: 0.5, color: "#64748b" }}>
        {meta.label} — Governance
      </Text>

      {nothing && (
        <div style={{ marginTop: 24 }}>
          <Empty description="No governance signals on this record" />
        </div>
      )}

      {riskField && (
        <>
          <Divider style={{ margin: "16px 0 8px" }} orientation="left" plain>
            AI-Act risk
          </Divider>
          <div>{tagFor(String(record[riskField]), RISK_COLORS)}</div>
          <Text type="secondary" style={{ fontSize: 12 }}>
            {labelOf(riskField)}
          </Text>
        </>
      )}

      {statusFields.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0 8px" }} orientation="left" plain>
            Workflow
          </Divider>
          {statusFields.map((name) => (
            <div key={name} style={{ marginBottom: 6 }}>
              {tagFor(String(record[name]), STATUS_COLORS)}
              <Text type="secondary" style={{ fontSize: 12, marginLeft: 4 }}>
                {labelOf(name)}
              </Text>
            </div>
          ))}
        </>
      )}

      {signalFields.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0 8px" }} orientation="left" plain>
            Signals
          </Divider>
          <Descriptions column={1} size="small">
            {signalFields.map((name) => (
              <Descriptions.Item key={name} label={labelOf(name)}>
                {renderCell(fieldMap.get(name), record[name])}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </>
      )}

      {relationFields.length > 0 && (
        <>
          <Divider style={{ margin: "16px 0 8px" }} orientation="left" plain>
            Related
          </Divider>
          <Descriptions column={1} size="small">
            {relationFields.map((f) => (
              <Descriptions.Item key={f.name} label={f.label ?? f.name}>
                {renderCell(f, record[f.name])}
              </Descriptions.Item>
            ))}
          </Descriptions>
        </>
      )}
    </div>
  );
};
